import { useMemo, useState } from 'react';

import {
  calculateMaklerpassStaffel,
  EXCLUDED_BROKERS,
  FREE_TERRITORY_SLOTS,
  type NewCustomerCandidate,
  type PartnerOnboardingStatus,
  TOP_BESTANDS_MAKLER,
  type TopBestandsMakler,
} from './partnerCatalog';

const STATUS_LABELS: Record<PartnerOnboardingStatus, string> = {
  ONBOARDED: 'Aktiv in Maklerpass',
  APPROVED_TARGET: 'Gesetzt (Von Dominik ausgewählt)',
  PRIO_A: 'Bundesliga-Standort (Prio A)',
  INVITED: 'Einladung gesendet',
  IN_TALKS: 'In Abstimmung',
};

const FULL_SERVICE_STEPS = [
  {
    nr: '01',
    title: 'Lead-Produktion & Volltranskript',
    desc: 'Eigentümer-Akquise inkl. Abhören-Verwertung, 2-Kanal-Stereo-Aufzeichnung und Wort-für-Wort-Transkript.',
  },
  {
    nr: '02',
    title: 'Terminierung & Kalender-Steuerung',
    desc: 'Direkte Terminbuchung in den Outlook-Kalender des Partners unter Beachtung der Gebiets- und Kalenderkapazität.',
  },
  {
    nr: '03',
    title: 'Terminbestätigung & Terminmappe',
    desc: 'Automatische E-Mail-Bestätigung, telefonische Vorab-Bestätigung am Vortag sowie PDF-Terminmappe inkl. Audio & Transkript.',
  },
  {
    nr: '04',
    title: 'Unterlagen-Service & Vollmacht',
    desc: 'Zusammenstellung und Einholung fehlender Objektunterlagen (Grundbuch, Energieausweis, Teilungserklärung, Mieterlisten).',
  },
  {
    nr: '05',
    title: 'Vertragsgestaltung & 24h-Nachfassen',
    desc: 'Auftrags-Konfigurator im Partner-/Leder-Design, digitale Unterschrift und systematisches Nachfassen bis „Auftrag erteilt“.',
  },
  {
    nr: '06',
    title: 'Magazin-Exposé & KI-Bildstudio',
    desc: 'Exposé-Erstellung (Layout 1 Pfau / Der Pass), KI-Entrümpelung, Virtual Staging und Grundriss-Aufbereitung.',
  },
  {
    nr: '07',
    title: 'Immowelt- & Portal-Inserat',
    desc: 'Nach Freigabe durch den Makler direkte Übernahme ins Inserat (OpenImmo 1.2.7 / Immowelt-Schnittstelle).',
  },
  {
    nr: '08',
    title: 'Käuferanfragen & Vorqualifizierung',
    desc: 'Bearbeitung eingehender Interessenten-Anfragen, Bonitäts-Vorprüfung und Koordination der Einzelbesichtigungen.',
  },
];

// MaklerpassPartnerSection: 3 Sparten (1. Top-Bestandsmakler mit Sales & Auftrag erteilt, 2. Neukunden / freie Gebiete, 3. Staffel- & Full-Service-Modell).
export function MaklerpassPartnerSection({ initialTab = 'bestand' }: { initialTab?: 'bestand' | 'neukunden' | 'modell' }) {
  const [subTab, setSubTab] = useState<'bestand' | 'neukunden' | 'modell'>(initialTab);
  const [search, setSearch] = useState('');
  const [statusMap, setStatusMap] = useState<Record<string, PartnerOnboardingStatus>>(() =>
    Object.fromEntries(TOP_BESTANDS_MAKLER.map((m) => [m.kd, m.status])),
  );
  const [selectedPitch, setSelectedPitch] = useState<TopBestandsMakler | null>(null);

  // Sparte 2: Neukunden-Pipeline mit automatischer Gebiets-Schutz-Prüfung (max. 1 Partner je PLZ-Leitregion)
  const [candidates, setCandidates] = useState<NewCustomerCandidate[]>([
    {
      id: 'nk-1',
      firma: 'SCHWARZundSAUER Immobilien',
      person: 'Marc Sauer',
      plz2: '70',
      regionName: 'Stuttgart-Innenstadt / Nord / West',
      email: 'ms@schwarzsauer-immobilien.de',
      telefon: '+49 1575 4455542',
      stage: 'QUALIFIZIERT',
      note: 'Aktiver Stuttgarter Partner mit freiem Kontingent (2/Woche) – idealer Kandidat für Maklerpass Full-Service.',
    },
    {
      id: 'nk-2',
      firma: 'Heimathafen Hering Immobilien e.K.',
      person: 'Patrick Hering',
      plz2: '73',
      regionName: 'Esslingen / Göppingen / Ostwürttemberg',
      email: 'p.hering@heimathafen-hering.de',
      telefon: '+49 1522 2022666',
      stage: 'QUALIFIZIERT',
      note: '79 gebuchte PLZs in der Region 70/73/74, offenes Kontingent.',
    },
  ]);
  const [newFirma, setNewFirma] = useState('');
  const [newPerson, setNewPerson] = useState('');
  const [newPlz2, setNewPlz2] = useState('60');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [collisionAlert, setCollisionAlert] = useState('');

  // Sparte 3: Interaktiver Staffel-Simulator
  const [simPrice, setSimPrice] = useState(550_000);
  const [simComm, setSimComm] = useState(7.14);
  const staffel = useMemo(() => calculateMaklerpassStaffel(simPrice, simComm), [simPrice, simComm]);

  const occupiedPlz2 = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of TOP_BESTANDS_MAKLER) {
      if ((statusMap[m.kd] ?? m.status) === 'ONBOARDED' || (statusMap[m.kd] ?? m.status) === 'IN_TALKS') {
        map.set(m.plz2, `${m.firma} (${m.person})`);
      }
    }
    for (const c of candidates) {
      if (c.stage === 'AKTIV' || c.stage === 'VERTRAG_GESENDET') {
        map.set(c.plz2, `${c.firma} (${c.person})`);
      }
    }
    return map;
  }, [statusMap, candidates]);

  const filteredBestand = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return TOP_BESTANDS_MAKLER;
    return TOP_BESTANDS_MAKLER.filter(
      (m) =>
        m.firma.toLowerCase().includes(q) ||
        m.person.toLowerCase().includes(q) ||
        m.plz2.includes(q) ||
        m.regionName.toLowerCase().includes(q) ||
        m.kd.includes(q),
    );
  }, [search]);

  const totalSales = useMemo(() => TOP_BESTANDS_MAKLER.reduce((acc, m) => acc + m.salesCount, 0), []);
  const totalMandates = useMemo(() => TOP_BESTANDS_MAKLER.reduce((acc, m) => acc + m.auftragCount, 0), []);

  const addCandidate = (e: React.FormEvent) => {
    e.preventDefault();
    const plzClean = newPlz2.trim().slice(0, 2);
    const existingChampion = TOP_BESTANDS_MAKLER.find((m) => m.plz2 === plzClean && (statusMap[m.kd] ?? m.status) === 'ONBOARDED');
    if (existingChampion) {
      setCollisionAlert(
        `Gebiets-Schutz aktiv: Leitregion PLZ ${plzClean} ist bereits exklusiv durch ${existingChampion.firma} (${existingChampion.person}) besetzt. Maximal 1 Partner je Gebiet!`,
      );
      return;
    }
    setCollisionAlert('');
    const slot = FREE_TERRITORY_SLOTS.find((s) => s.plz2 === plzClean);
    setCandidates((prev) => [
      {
        id: `nk-${Date.now()}`,
        firma: newFirma.trim(),
        person: newPerson.trim(),
        plz2: plzClean,
        regionName: slot?.regionName ?? `Leitregion PLZ ${plzClean}`,
        email: newEmail.trim(),
        telefon: newPhone.trim(),
        stage: 'NEU',
        note: 'Gebiets-Exklusivität geprüft (1 Partner je Gebiet).',
      },
      ...prev,
    ]);
    setNewFirma('');
    setNewPerson('');
    setNewEmail('');
    setNewPhone('');
  };

  return (
    <div className="stack">
      <section className="card" style={{ borderLeft: '4px solid #C8A24E' }}>
        <div className="split" style={{ alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <p className="eyebrow">Maklerpass Exklusiv-Partnerprogramm · Nur ausgewählte Top-Makler</p>
            <h2 style={{ margin: '0.2rem 0 0.4rem' }}>
              Auftrag erteilt = Abrechnung + gestaffelte Verkaufsbeteiligung (~5 %)
            </h2>
            <p className="muted" style={{ maxWidth: '78ch', margin: 0 }}>
              Wir holen gezielt nur unsere besten Makler mit nachgewiesenen <strong>Sales</strong> und{' '}
              <strong>Auftrag erteilt</strong> aus <code>backend.portal.maklerplan.ch</code> rein (strikt{' '}
              <strong>maximal 1 Partner pro PLZ-Gebiet</strong>) und besetzen freie Regionen über die Neukunden-Sparte.
              Für diese Partner übernehmen wir den kompletten Full-Service von der Terminierung bis zum fertigen Exposé,
              Vertrag und Immowelt-Inserat.
            </p>
          </div>
          <div className="form-actions" style={{ flexWrap: 'wrap' }}>
            <button
              type="button"
              className={subTab === 'bestand' ? 'btn btn-primary' : 'btn'}
              onClick={() => setSubTab('bestand')}
            >
              1. Top-Bestandsmakler ({TOP_BESTANDS_MAKLER.length} · 1 je Gebiet)
            </button>
            <button
              type="button"
              className={subTab === 'neukunden' ? 'btn btn-primary' : 'btn'}
              onClick={() => setSubTab('neukunden')}
            >
              2. Neukunden &amp; freie Gebiete ({FREE_TERRITORY_SLOTS.length})
            </button>
            <button
              type="button"
              className={subTab === 'modell' ? 'btn btn-primary' : 'btn'}
              onClick={() => setSubTab('modell')}
            >
              3. Staffel- &amp; Full-Service-Modell (~5 %)
            </button>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            gap: '0.75rem',
            marginTop: '1rem',
            paddingTop: '0.85rem',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <div>
            <span className="muted small">Exklusive Top-Makler (1 je PLZ-Region)</span>
            <div style={{ fontSize: '1.35rem', fontWeight: 700 }}>{TOP_BESTANDS_MAKLER.length} Regionen</div>
          </div>
          <div>
            <span className="muted small">Nachgewiesene Abschlüsse im Pool</span>
            <div style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--color-primary)' }}>
              {totalSales} Sales · {totalMandates} Aufträge
            </div>
          </div>
          <div>
            <span className="muted small">Freie Neukunden-Gebiete</span>
            <div style={{ fontSize: '1.35rem', fontWeight: 700 }}>{FREE_TERRITORY_SLOTS.length} PLZ-Leitregionen</div>
          </div>
          <div>
            <span className="muted small">Abrechnungs-Prinzip</span>
            <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>Auftrag erteilt + ~5 % Staffel</div>
          </div>
        </div>
      </section>

      {subTab === 'bestand' && (
        <section className="card" aria-label="Top-Bestandsmakler mit Sales und Auftrag erteilt">
          <div className="split" style={{ marginBottom: '0.85rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <h3 style={{ margin: 0 }}>
                Sparte 1: Top-Bestandsmakler aus <code>backend.portal.maklerplan.ch</code> (mit Sales &amp; Auftrag erteilt)
              </h3>
              <p className="muted small" style={{ margin: '0.2rem 0 0' }}>
                Gebiets-Schutz aktiv: Pro 2-stelliger PLZ-Leitregion ist exakt der #1-Partner mit den meisten Verkäufen
                und erteilten Makleraufträgen gelistet – keine Überbelegung in einem Gebiet.
              </p>
            </div>
            <input
              type="search"
              placeholder="PLZ, Region, Firma oder Name filtern …"
              aria-label="Top-Makler filtern"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: '260px' }}
            />
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Exklusiv-Gebiet (PLZ)</th>
                  <th>Kd.-Nr. &amp; Maklerfirma</th>
                  <th>Sales (Verkäufe)</th>
                  <th>Auftrag erteilt</th>
                  <th>Leads (Historie)</th>
                  <th>Reklamationsquote</th>
                  <th>Kontakt</th>
                  <th>Maklerpass-Status</th>
                  <th>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {filteredBestand.map((m) => {
                  const st = statusMap[m.kd] ?? m.status;
                  return (
                    <tr key={m.kd}>
                      <td>
                        <span className="badge badge-ok">PLZ {m.plz2}</span>
                        <br />
                        <small className="muted">{m.regionName}</small>
                        {m.ligaBadge && (
                          <>
                            <br />
                            <small style={{ color: 'var(--color-primary)', fontWeight: 600 }}>{m.ligaBadge}</small>
                          </>
                        )}
                      </td>
                      <td>
                        <strong>{m.firma}</strong>
                        <br />
                        <small className="muted">
                          #{m.kd} · {m.person}
                        </small>
                      </td>
                      <td>
                        <strong style={{ fontSize: '1.05rem', color: 'var(--color-primary)' }}>{m.salesCount} Sales</strong>
                      </td>
                      <td>
                        <strong>{m.auftragCount} Aufträge</strong>
                      </td>
                      <td>{m.leadsGesamt}</td>
                      <td>
                        <strong style={{ color: m.reklamationsquote >= 20 ? '#b45309' : 'inherit' }}>
                          {m.reklamationsquote.toFixed(1).replace('.', ',')} %
                        </strong>
                        {m.hinweis && (
                          <>
                            <br />
                            <small style={{ color: '#b45309' }}>{m.hinweis}</small>
                          </>
                        )}
                      </td>
                      <td>
                        <a href={`mailto:${m.email}`}>{m.email}</a>
                        <br />
                        <small className="muted">{m.telefon}</small>
                      </td>
                      <td>
                        <select
                          aria-label={`Status ${m.firma}`}
                          value={st}
                          onChange={(e) =>
                            setStatusMap((prev) => ({ ...prev, [m.kd]: e.target.value as PartnerOnboardingStatus }))
                          }
                        >
                          {Object.entries(STATUS_LABELS).map(([k, lbl]) => (
                            <option key={k} value={k}>
                              {lbl}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button type="button" className="btn btn-small btn-primary" onClick={() => setSelectedPitch(m)}>
                          Reinholen / Angebot
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <details style={{ marginTop: '1rem' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
              Ausgeschlossen ({EXCLUDED_BROKERS.length}) – Reklamationen, respektlose Schreiben 2020–2026 oder Gebiets-Exklusivität
            </summary>
            <div className="table-wrap" style={{ marginTop: '0.6rem' }}>
              <table>
                <thead>
                  <tr>
                    <th>Kd.-Nr. &amp; Firma</th>
                    <th>Grund</th>
                    <th>Beleg</th>
                  </tr>
                </thead>
                <tbody>
                  {EXCLUDED_BROKERS.map((b) => (
                    <tr key={b.kd}>
                      <td>
                        <strong>{b.firma}</strong>
                        <br />
                        <small className="muted">#{b.kd}</small>
                      </td>
                      <td>{b.grund}</td>
                      <td className="small">{b.beleg}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          {selectedPitch && (
            <div
              className="card"
              style={{ marginTop: '1rem', background: 'var(--color-bg)', borderLeft: '4px solid #C8A24E' }}
            >
              <div className="split">
                <strong>
                  Exklusiv-Einladung an {selectedPitch.person} ({selectedPitch.firma} · Gebiet PLZ {selectedPitch.plz2}xx)
                </strong>
                <button type="button" className="btn btn-small" onClick={() => setSelectedPitch(null)}>
                  Schließen
                </button>
              </div>
              <p className="small" style={{ whiteSpace: 'pre-line', marginTop: '0.5rem' }}>
                {`Betreff: Exklusiv-Einladung zu Maklerpass für das Gebiet PLZ ${selectedPitch.plz2} (${selectedPitch.regionName}) – Full-Service & Abrechnung nach „Auftrag erteilt“\n\nHallo ${selectedPitch.person},\n\nmit ${selectedPitch.salesCount} bestätigten Immobilienverkäufen und ${selectedPitch.auftragCount} erteilten Makleraufträgen gehören Sie in unserer Auswertung zu den erfolgreichsten Partnern überhaupt.\n\nDeshalb laden wir Sie exklusiv (als einzigen Partner für die Leitregion PLZ ${selectedPitch.plz2} – ${selectedPitch.regionName}) in unser neues Maklerpass Full-Service-Programm ein:\n• Wir übernehmen für Sie: Vorqualifizierung, Terminierung in Ihren Kalender, E-Mail- & telefonische Terminbestätigung, komplette Unterlagenbeschaffung, Vertragsgestaltung (inkl. 24h-Nachfassen), Magazin-Exposé mit KI-Bildstudio, Immowelt-Inserat und die Vorqualifizierung eingehender Käuferanfragen.\n• Faires Prinzip „Auftrag erteilt = Abrechnung“: Abgerechnet wird nach unserer Staffel erst, wenn der Maklerauftrag erteilt ist – kombiniert mit einer fair gestaffelten Verkaufsbeteiligung (~5 %).\n\nLassen Sie uns diese Woche kurz telefonieren, um Ihr Gebiet PLZ ${selectedPitch.plz2} fest für Sie zu reservieren.`}
              </p>
              <div className="form-actions">
                <a
                  className="btn btn-small btn-primary"
                  href={`mailto:${selectedPitch.email}?subject=${encodeURIComponent(`Exklusiv-Einladung zu Maklerpass für PLZ ${selectedPitch.plz2} (${selectedPitch.regionName})`)}`}
                  onClick={() => setStatusMap((prev) => ({ ...prev, [selectedPitch.kd]: 'INVITED' }))}
                >
                  E-Mail-Programm öffnen &amp; als „Eingeladen“ markieren
                </a>
              </div>
            </div>
          )}
        </section>
      )}

      {subTab === 'neukunden' && (
        <div className="stack">
          <section className="card" aria-label="Neukunden für Maklerpass aufnehmen">
            <h3 style={{ marginTop: 0 }}>Sparte 2: Neukunden für Maklerpass (mit Gebiets-Kollisionsprüfung)</h3>
            <p className="muted small">
              Neue Makler werden nur in Regionen aufgenommen, in denen noch kein exklusiver Maklerpass-Partner aktiv ist
              (strikt 1 Partner je PLZ-Leitregion).
            </p>
            {collisionAlert && <p className="alert alert-warning">{collisionAlert}</p>}
            <form onSubmit={addCandidate} className="form-grid" style={{ marginTop: '0.75rem' }}>
              <label className="field">
                <span>Maklerfirma *</span>
                <input required value={newFirma} onChange={(e) => setNewFirma(e.target.value)} placeholder="z. B. Rhein-Main Exklusiv Immobilien GmbH" />
              </label>
              <label className="field">
                <span>Ansprechpartner *</span>
                <input required value={newPerson} onChange={(e) => setNewPerson(e.target.value)} placeholder="Vor- und Nachname" />
              </label>
              <label className="field">
                <span>PLZ-Leitregion (2-stellig) *</span>
                <select value={newPlz2} onChange={(e) => setNewPlz2(e.target.value)}>
                  {FREE_TERRITORY_SLOTS.map((s) => (
                    <option key={s.plz2} value={s.plz2}>
                      PLZ {s.plz2}xx – {s.regionName} ({s.potentialLeadsMonth} Leads/Mon.)
                    </option>
                  ))}
                  <option value="14">PLZ 14xx – Berlin/Potsdam (Bereits belegt: Test Kollisionsschutz)</option>
                </select>
              </label>
              <label className="field">
                <span>E-Mail *</span>
                <input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="kontakt@makler.de" />
              </label>
              <label className="field">
                <span>Telefon *</span>
                <input required value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="+49 17..." />
              </label>
              <div className="field" style={{ justifyContent: 'flex-end' }}>
                <button type="submit" className="btn btn-primary">
                  Gebiet prüfen &amp; Neukunde aufnehmen
                </button>
              </div>
            </form>
          </section>

          <section className="card" aria-label="Neukunden-Pipeline">
            <h3 style={{ marginTop: 0 }}>Aktuelle Neukunden-Kandidaten ({candidates.length})</h3>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Gebiet</th>
                    <th>Firma &amp; Ansprechpartner</th>
                    <th>Kontakt</th>
                    <th>Status</th>
                    <th>Notiz</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <span className="badge badge-ok">PLZ {c.plz2}xx</span>
                        <br />
                        <small className="muted">{c.regionName}</small>
                      </td>
                      <td>
                        <strong>{c.firma}</strong>
                        <br />
                        <small className="muted">{c.person}</small>
                      </td>
                      <td>
                        <a href={`mailto:${c.email}`}>{c.email}</a>
                        <br />
                        <small className="muted">{c.telefon}</small>
                      </td>
                      <td>
                        <span className="badge badge-muted">{c.stage}</span>
                      </td>
                      <td className="small">{c.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="card" aria-label="Freie Exklusiv-Gebiete">
            <h3 style={{ marginTop: 0 }}>Freie Exklusiv-Gebiete für die Neukunden-Akquise (maximal 1 Partner je Gebiet)</h3>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>PLZ-Leitregion</th>
                    <th>Region / Ballungsraum</th>
                    <th>Bundesland</th>
                    <th>Eigentümer-Potenzial</th>
                    <th>Gebiets-Status</th>
                  </tr>
                </thead>
                <tbody>
                  {FREE_TERRITORY_SLOTS.map((s) => {
                    const holder = occupiedPlz2.get(s.plz2);
                    return (
                      <tr key={s.plz2}>
                        <td>
                          <strong>PLZ {s.plz2}xx</strong>
                        </td>
                        <td>{s.regionName}</td>
                        <td>{s.bundesland}</td>
                        <td>ca. {s.potentialLeadsMonth} qualifizierte Leads / Monat</td>
                        <td>
                          {holder ? (
                            <span className="badge badge-warn">Reserviert ({holder})</span>
                          ) : (
                            <span className="badge badge-ok">Frei (1 Exklusiv-Platz)</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {subTab === 'modell' && (
        <div className="stack">
          <section className="card" aria-label="Staffelrechner Auftrag erteilt und Verkaufsbeteiligung">
            <h3 style={{ marginTop: 0 }}>
              Sparte 3: Das Maklerpass-Prinzip „Auftrag erteilt = Abrechnung + gestaffelte Verkaufsbeteiligung (~5 %)“
            </h3>
            <p className="muted small">
              Nur für ausgewählte Top-Kunden: Wir produzieren den Lead, terminieren, bestätigen den Termin, fassen nach,
              erstellen Vertrag &amp; Magazin-Exposé, bestücken Immowelt und qualifizieren eingehende Käuferanfragen vor.
            </p>

            <div className="form-grid" style={{ marginTop: '1rem' }}>
              <label className="field">
                <span>
                  Objekt-Verkaufspreis: <strong>{simPrice.toLocaleString('de-DE')} €</strong>
                </span>
                <input
                  type="range"
                  min={150_000}
                  max={5_000_000}
                  step={50_000}
                  value={simPrice}
                  onChange={(e) => setSimPrice(Number(e.target.value))}
                />
              </label>
              <label className="field">
                <span>
                  Gesamtprovision (Käufer + Verkäufer inkl. MwSt.): <strong>{simComm.toFixed(2).replace('.', ',')} %</strong>
                </span>
                <input
                  type="range"
                  min={3.57}
                  max={7.14}
                  step={0.01}
                  value={simComm}
                  onChange={(e) => setSimComm(Number(e.target.value))}
                />
              </label>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                gap: '0.85rem',
                marginTop: '1rem',
                padding: '1rem',
                borderRadius: '10px',
                background: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
              }}
            >
              <div>
                <span className="muted small">1. Abrechnung bei „Auftrag erteilt“</span>
                <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>
                  {staffel.auftragFeeEur.toLocaleString('de-DE')} €
                </div>
                <small className="muted">{staffel.auftragTierLabel}</small>
              </div>
              <div>
                <span className="muted small">2. Verkaufs-Staffel (bei Notarvertrag)</span>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--color-primary)' }}>
                  {staffel.saleParticipationPercent.toFixed(1).replace('.', ',')} % Staffel (
                  {staffel.saleParticipationEur.toLocaleString('de-DE')} €)
                </div>
                <small className="muted">Gestaffelt nach Verkaufsvolumen</small>
              </div>
              <div>
                <span className="muted small">Gesamt-Vergütung Maklerpass</span>
                <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>
                  {staffel.totalMaklerpassRevenueEur.toLocaleString('de-DE')} €
                </div>
                <small className="muted">Inkl. komplettem Backoffice &amp; Inserat</small>
              </div>
              <div>
                <span className="muted small">Netto-Ertrag beim Partner-Makler</span>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#15803d' }}>
                  {staffel.brokerNetCommissionEur.toLocaleString('de-DE')} €
                </div>
                <small className="muted">Ohne eigenen Innendienst-Aufwand</small>
              </div>
            </div>
          </section>

          <section className="card" aria-label="Full-Service-Leistungspaket">
            <h3 style={{ marginTop: 0 }}>Was wir für ausgewählte Maklerpass-Partner komplett übernehmen (8-Stufen-Service)</h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '0.75rem',
                marginTop: '0.75rem',
              }}
            >
              {FULL_SERVICE_STEPS.map((s) => (
                <div
                  key={s.nr}
                  style={{
                    padding: '0.85rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg)',
                  }}
                >
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-primary)', letterSpacing: '0.14em' }}>
                    STUFE {s.nr}
                  </div>
                  <strong style={{ display: 'block', margin: '0.2rem 0 0.35rem' }}>{s.title}</strong>
                  <p className="muted small" style={{ margin: 0 }}>
                    {s.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
